import React from "react"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { Check } from "lucide-react"
import { cn } from "@/lib/utils"
import { UserAddressDto } from "@/api/api-client"

interface AddressSelectorProps {
  savedAddresses: UserAddressDto[]
  selectedAddressId: number | "new" | null
  isLoading?: boolean
  onSelect: (id: number | "new") => void
  text: any
}

export const AddressSelector: React.FC<AddressSelectorProps> = ({
  savedAddresses,
  selectedAddressId,
  isLoading,
  onSelect,
  text,
}) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Label className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground">
          {text.savedAddresses}
        </Label>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Skeleton className="h-[90px] w-full rounded-lg" />
          <Skeleton className="h-[90px] w-full rounded-lg" />
        </div>
        <Separator className="my-8 bg-foreground/5" />
      </div>
    )
  }

  // Always show the selector if it's not loading, even if savedAddresses is empty
  // so the user can select "New Address"
  return (
    <div className="space-y-4">
      <Label className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground">
        {text.savedAddresses}
      </Label>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {savedAddresses.map((addr) => (
          <Button
            key={addr.id}
            variant="ghost"
            type="button"
            onClick={() => onSelect(addr.id)}
            className={cn(
              "group relative flex h-auto flex-col items-start justify-start rounded-lg border-[1px] p-4 text-left transition-all duration-300",
              selectedAddressId === addr.id
                ? "border-accent bg-accent/5 shadow-lg"
                : "border-border bg-muted/50 hover:border-foreground/20"
            )}
          >
            {selectedAddressId === addr.id && (
              <div className="absolute right-0 top-0 flex h-6 w-6 items-center justify-center bg-accent">
                <Check className="h-3 w-3 text-white" />
              </div>
            )}
            <p className="mb-1 font-display text-lg tracking-wider text-foreground">
              {addr.receiverName}
            </p>
            <p className="line-clamp-1 text-xs font-bold uppercase text-muted-foreground">
              {addr.address.street} {addr.address.externalNumber}
            </p>
          </Button>
        ))}
        <Button
          variant="ghost"
          type="button"
          onClick={() => onSelect("new")}
          className={cn(
            "group relative flex h-auto flex-col items-center justify-center rounded-lg border-[1px] border-dashed p-4 text-center transition-all duration-300",
            selectedAddressId === "new"
              ? "border-accent bg-accent/5 shadow-lg"
              : "border-border bg-muted/50 hover:border-foreground/20"
          )}
        >
          <p className="font-display text-lg tracking-widest uppercase opacity-40">
            + {text.newAddress || "NEW ADDRESS"}
          </p>
        </Button>
      </div>
      <Separator className="my-8 bg-foreground/5" />
    </div>
  )
}

